import { useMemo } from 'react';
import { useMonitoringTrailing } from '../../../hooks/useMonitoringTrailing';
import type {
    HistoryArchiveStatus,
    HistoryBucket,
    MonitoringBacklogResponse
} from '../../../services/dmMonitoringOverviewHistory';
import { CardErrorState } from '../../accounts/overview/CardErrorState';
import { GranularityToggle } from '../../accounts/GranularityToggle';
import { ChartLegend } from '../../billing/overview/OverviewCharts';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import { ChartSkeleton } from '../../shared/ChartSkeleton';
import { ACTIVITY_CHART_HEIGHT_PX } from './ActivityChart';
import { backlogRows, BACKLOG_KEYS, hasBacklogData, unreliableSpan } from './backlogRows';
import { OpenBacklogChart } from './OpenBacklogChart';
import { BACKLOG_LEGEND } from './overviewLegend';
import type { ReportingPeriod } from './overviewPeriod';
import { MEASURE_OPTIONS } from './overviewSeries';
import { BUCKET_OPTIONS, bucketWindow } from './overTimeRows';
import { TOTAL_LABELS, type PerPersonMeasure } from './perPersonRows';
import { EmptyNote } from './StackedBar';
import { useStoredChoice } from './useStoredChoice';

const BUCKET_STORAGE_KEY = 'monitoring_overview_backlog_bucket';
const MEASURE_STORAGE_KEY = 'monitoring_overview_backlog_measure';
const ITEMS_INFO =
    'Everything still open at the end of each day, week or month, however long ago it arrived; unlike Activity, which counts what arrived in that period.';
const PATIENTS_INFO =
    'Patients with at least one notification or message still open at the end of each day, week or month; a patient with both counts once in the total.';

function backlogCopy(measure: PerPersonMeasure, trailing: ReportingPeriod) {
    if (measure === 'events') {
        return {
            subtitle: trailing.label,
            info: ITEMS_INFO,
            empty: `No backlog data ${trailing.phrase}`
        };
    }
    return {
        subtitle: `Patients with open items · ${trailing.label.toLowerCase()}`,
        info: PATIENTS_INFO,
        empty: `No patient data ${trailing.phrase}`
    };
}
const ERROR_MESSAGE = "Couldn't load the open backlog";

const UNAVAILABLE_NOTES: Readonly<Record<Exclude<HistoryArchiveStatus, 'included'>, string>> = {
    not_configured: "The open backlog needs older history, which isn't connected here.",
    failed: "Older history couldn't be loaded, so the open backlog can't be counted."
};

function ChartSlot({ children }: { children: React.ReactNode }) {
    return (
        <div className="grid place-items-center" style={{ height: ACTIVITY_CHART_HEIGHT_PX }}>
            {children}
        </div>
    );
}

interface BacklogBodyProps {
    backlog: MonitoringBacklogResponse;
    measure: PerPersonMeasure;
    emptyMessage: string;
}

function BacklogBody({ backlog, measure, emptyMessage }: BacklogBodyProps) {
    const rows = useMemo(
        () => backlogRows(backlog.series, backlog.bucket, measure),
        [backlog, measure]
    );
    const unreliable = useMemo(
        () => unreliableSpan(rows, backlog.reliableFrom),
        [rows, backlog.reliableFrom]
    );
    if (backlog.archive !== 'included') {
        return (
            <ChartSlot>
                <EmptyNote>{UNAVAILABLE_NOTES[backlog.archive]}</EmptyNote>
            </ChartSlot>
        );
    }
    if (!hasBacklogData(rows)) {
        return (
            <ChartSlot>
                <EmptyNote>{emptyMessage}</EmptyNote>
            </ChartSlot>
        );
    }
    return (
        <div className="flex flex-col gap-3">
            <OpenBacklogChart
                rows={rows}
                bucket={backlog.bucket}
                unreliable={unreliable}
                totalLabel={TOTAL_LABELS[measure]}
            />
            <ChartLegend items={BACKLOG_KEYS.map((key) => BACKLOG_LEGEND[key])} />
        </div>
    );
}

interface BacklogTogglesProps {
    measure: PerPersonMeasure;
    onMeasureChange: (next: PerPersonMeasure) => void;
    bucket: HistoryBucket;
    onBucketChange: (next: HistoryBucket) => void;
}

function BacklogToggles({ measure, onMeasureChange, bucket, onBucketChange }: BacklogTogglesProps) {
    return (
        <div className="flex flex-wrap items-center justify-end gap-2">
            <GranularityToggle
                value={measure}
                options={MEASURE_OPTIONS}
                onChange={onMeasureChange}
                ariaLabel="Open backlog: count items or patients"
            />
            <GranularityToggle
                value={bucket}
                options={BUCKET_OPTIONS}
                onChange={onBucketChange}
                ariaLabel="Open backlog: day, week or month"
            />
        </div>
    );
}

export function OpenBacklogCard() {
    const [bucket, setBucket] = useStoredChoice(BUCKET_STORAGE_KEY, BUCKET_OPTIONS);
    const [measure, setMeasure] = useStoredChoice(MEASURE_STORAGE_KEY, MEASURE_OPTIONS);
    const backlog = useMonitoringTrailing('backlog', bucket);
    // Labels follow the bucket the data is for, so the previous bucket never wears the new name.
    const copy = backlogCopy(measure, bucketWindow(backlog.data?.bucket ?? bucket));
    return (
        <OverviewCard
            title="Open backlog"
            subtitle={copy.subtitle}
            info={copy.info}
            wrapAction
            action={
                <BacklogToggles
                    measure={measure}
                    onMeasureChange={setMeasure}
                    bucket={bucket}
                    onBucketChange={setBucket}
                />
            }
        >
            {backlog.data ? (
                <div className={`transition-opacity ${backlog.isSwitching ? 'opacity-60' : ''}`}>
                    <BacklogBody
                        backlog={backlog.data}
                        measure={measure}
                        emptyMessage={copy.empty}
                    />
                </div>
            ) : (
                <ChartSlot>
                    {backlog.isError ? (
                        <CardErrorState
                            message={ERROR_MESSAGE}
                            onRetry={() => void backlog.retry()}
                        />
                    ) : (
                        <div className="h-full w-full">
                            <ChartSkeleton />
                        </div>
                    )}
                </ChartSlot>
            )}
        </OverviewCard>
    );
}
